import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { ConfigSelect, type ConfigSelectOption } from '../../components/shared/ConfigSelect';
import '../../styles/SharingSettings.css';
import { showMessage } from '../../showMessage';
import { buildSharingLabel, sharingBadgeClass } from '../shared/sharingUtils';

import type { SharingMetadata, SharedUser } from '../../types';

type SharingListItem = {
  resourceType: 'boat' | 'event' | 'file';
  resourceId: number;
  name: string;
  boatName?: string | null;
  eventName?: string | null;
  sharing?: SharingMetadata;
};

type ResourceOption = {
  id: number;
  label: string;
  metadata?: SharingMetadata;
  parentId?: number | null;
};

type ApiSharingResponse = {
  success: boolean;
  sharedByMe?: SharingListItem[];
  sharedWithMe?: SharingListItem[];
  message?: string;
};

type ApiBoatResponse = {
  success: boolean;
  userBoats?: Array<{ id: number; name: string; sharing?: SharingMetadata }>;
};

type ApiEventsResponse = {
  success: boolean;
  events?: Array<{ id: number; event_name: string; event_date?: string | null; sharing?: SharingMetadata }>;
};

type ApiEventFilesResponse = {
  success: boolean;
  event?: {
    files?: Array<{ id: number; name?: string | null; display_name?: string | null; sharing?: SharingMetadata }>;
    eventName?: string;
  };
};

const K_NOTIF_ERROR = 'notifications.error' as const;
const K_NOTIF_SUCCESS = 'notifications.success' as const;
const K_ERR_API = 'errors.missingApiBase' as const;
const K_ERR_TOKEN = 'errors.missingToken' as const;
const K_ERR_UNEXPECTED = 'modals.unexpectedError' as const;
const K_SHARE_LABEL = 'settings.privacy.shareData' as const;
const K_SHARED_DATA_TITLE = 'settings.privacy.sharedDataTitle' as const;
const K_SHARED_BY_LABEL = 'settings.privacy.sharedBy' as const;
const PLACEHOLDER_BOAT = 'race.placeholders.boat' as const;
const PLACEHOLDER_EVENT = 'race.placeholders.event' as const;
const PLACEHOLDER_FILE = 'common.file' as const;
const SELECT_RESOURCE_LABEL = 'common.select' as const;
const SELECT_RESOURCE_MESSAGE = 'sharing.selectResource' as const;
const SHARE_BOAT = 'sharing.shareBoat' as const;
const SHARE_BOATS = 'sharing.shareBoats' as const;
const SHARE_EVENT = 'sharing.shareEvent' as const;
const SHARE_EVENTS = 'sharing.shareEvents' as const;
const SHARE_FILE = 'sharing.shareFile' as const;
const SHARE_FILES = 'sharing.shareFiles' as const;

const RESOURCE_TYPE_LABEL_KEYS: Record<SharingListItem['resourceType'], string> = {
  boat: 'sharing.resourceTypes.boat',
  event: 'sharing.resourceTypes.event',
  file: 'sharing.resourceTypes.file',
} as const;

const RESOURCE_TYPE_LABEL_FALLBACKS: Record<SharingListItem['resourceType'], string> = {
  boat: 'Barco',
  event: 'Evento',
  file: 'Archivo',
};

const getSharedUserDisplayName = (user?: SharedUser | null): string => {
  if (!user) return '';
  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }
  return user.email ?? '';
};

export const SharingSettings = () => {
  const { t } = useTranslation('common');
  const translateSharing = useCallback(
    (key: string, fallback?: string) => t(key, { defaultValue: fallback }),
    [t],
  );
  const [sharedByMe, setSharedByMe] = useState<SharingListItem[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<SharingListItem[]>([]);
  const [ownedBoats, setOwnedBoats] = useState<ResourceOption[]>([]);
  const [ownedEvents, setOwnedEvents] = useState<ResourceOption[]>([]);
  const [ownedFiles, setOwnedFiles] = useState<ResourceOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [updatingCommentPermission, setUpdatingCommentPermission] = useState<string | null>(null);
  const [selectedBoatIds, setSelectedBoatIds] = useState<Set<string>>(new Set());
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{
    email: string;
    canViewComments: boolean;
  }>({
    email: '',
    canViewComments: false,
  });

  const apiBase = process.env.REACT_APP_API_BASE_URL ?? '';

  const boatSelectOptions: ConfigSelectOption[] = useMemo(
    () => ownedBoats.map((option) => ({ value: String(option.id), label: option.label })),
    [ownedBoats],
  );
  const eventSelectOptions: ConfigSelectOption[] = useMemo(
    () => ownedEvents.map((option) => ({ value: String(option.id), label: option.label })),
    [ownedEvents],
  );
  const fileSelectOptions: ConfigSelectOption[] = useMemo(
    () => ownedFiles.map((option) => ({ value: String(option.id), label: option.label })),
    [ownedFiles],
  );

  const toggleSelection = (
    value: string,
    updater: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => {
    updater((previous) => {
      const next = new Set(previous);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const shareTargetLabel = useMemo(() => {
    const filesCount = selectedFileIds.size;
    const eventsCount = selectedEventIds.size;
    const boatsCount = selectedBoatIds.size;
    if (filesCount > 0) {
      return filesCount > 1 ? t(SHARE_FILES, 'Compartir archivos') : t(SHARE_FILE, 'Compartir archivo');
    }
    if (eventsCount > 0) {
      return eventsCount > 1 ? t(SHARE_EVENTS, 'Compartir eventos') : t(SHARE_EVENT, 'Compartir evento');
    }
    if (boatsCount > 0) {
      return boatsCount > 1 ? t(SHARE_BOATS, 'Compartir barcos') : t(SHARE_BOAT, 'Compartir barco');
    }
    return t(K_SHARE_LABEL, 'Compartir');
  }, [selectedBoatIds, selectedEventIds, selectedFileIds, t]);

  const selectedCountLabel = (count: number) =>
    count === 0
      ? undefined
      : t('sharing.itemsSelected', {
          count,
          defaultValue: count === 1 ? '1 seleccionado' : `${count} seleccionados`,
        });

  const setsAreEqual = (first: Set<string>, second: Set<string>) =>
    first.size === second.size && [...first].every((value) => second.has(value));

  const fetchSharingOverview = useCallback(async () => {
    if (!apiBase) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_API));
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_TOKEN));
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await fetch(`${apiBase}/api/sharing/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json: unknown = await response.json();
      const data = json as ApiSharingResponse;
      if (data?.success) {
        setSharedByMe(Array.isArray(data.sharedByMe) ? data.sharedByMe : []);
        setSharedWithMe(Array.isArray(data.sharedWithMe) ? data.sharedWithMe : []);
      } else {
        showMessage('error', t(K_NOTIF_ERROR), data.message ?? t(K_ERR_UNEXPECTED));
      }
    } catch (error) {
      console.error('Error fetching sharing overview:', error);
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_UNEXPECTED));
    } finally {
      setIsRefreshing(false);
    }
  }, [apiBase, t]);

  const fetchOwnedBoats = useCallback(async () => {
    if (!apiBase) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const boatsResponse = await fetch(`${apiBase}/api/user/getUserBoats`, {
        method: 'POST',
        headers,
      });
      const boatsJson: unknown = await boatsResponse.json();
      const boatsData = boatsJson as ApiBoatResponse;
      const ownedBoatsOptions =
        boatsData?.success && Array.isArray(boatsData.userBoats)
          ? boatsData.userBoats
              .filter((boat) => (boat.sharing ? boat.sharing.isOwner : true))
              .map((boat) => ({
                id: boat.id,
                label: boat.name,
                metadata: boat.sharing,
              }))
          : [];
      setOwnedBoats(ownedBoatsOptions);
    } catch (error) {
      console.error('Error fetching boats for sharing:', error);
    }
  }, [apiBase]);

  const fetchEventsForBoat = useCallback(
    async (boatId: number, boatLabel: string) => {
      if (!apiBase || !boatId) {
        return [];
      }
      const token = localStorage.getItem('token');
      if (!token) return [];
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };
        const eventsResponse = await fetch(`${apiBase}/api/user/getEvents`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ boat_id: boatId }),
        });
        const eventsJson: unknown = await eventsResponse.json();
        const eventsData = eventsJson as ApiEventsResponse;
        const ownedEventsList =
          eventsData?.success && Array.isArray(eventsData.events)
            ? eventsData.events.filter((event) => (event.sharing ? event.sharing.isOwner : true))
            : [];
        return ownedEventsList.map((event) => {
          const dateLabel =
            event.event_date && !Number.isNaN(new Date(event.event_date).getTime())
              ? new Date(event.event_date).toLocaleDateString()
              : null;
          const labelBase = `${event.event_name}${dateLabel ? ' · ' + dateLabel : ''}`;
          return {
            id: event.id,
            label: `${labelBase} (${boatLabel})`,
            metadata: event.sharing,
            parentId: boatId,
          };
        });
      } catch (error) {
        console.error('Error fetching events for sharing:', error);
        return [];
      }
    },
    [apiBase],
  );

  const fetchFilesForEvent = useCallback(
    async (eventId: number, eventLabel: string) => {
      if (!apiBase || !eventId) {
        return [];
      }
      const token = localStorage.getItem('token');
      if (!token) return [];
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };
        const filesResponse = await fetch(`${apiBase}/api/user/getEventsFiles`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ event_id: eventId, filter: 'all' }),
        });
        const filesJson: unknown = await filesResponse.json();
        const filesData = filesJson as ApiEventFilesResponse;
        const files = filesData?.event?.files ?? [];
        return files
          .filter((file) => (file as { sharing?: SharingMetadata }).sharing?.isOwner ?? true)
          .map((file) => {
            const fileName =
              (typeof file.display_name === 'string' && file.display_name.trim().length > 0
                ? file.display_name.trim()
                : file.name) ?? t('common.file', 'Archivo');
            return {
              id: file.id,
              label: `${fileName} (${eventLabel})`,
              metadata: (file as { sharing?: SharingMetadata }).sharing,
              parentId: eventId,
            };
          });
      } catch (error) {
        console.error('Error fetching files for sharing:', error);
        return [];
      }
    },
    [apiBase, t],
  );

  useEffect(() => {
    void fetchSharingOverview();
    void fetchOwnedBoats();
  }, [fetchOwnedBoats, fetchSharingOverview]);

  // When boat selection changes, update events and cascade clears
  useEffect(() => {
    const run = async () => {
      const eventAccumulator: ResourceOption[] = [];
      for (const boatId of selectedBoatIds) {
        const boat = ownedBoats.find((b) => String(b.id) === boatId);
        const events = await fetchEventsForBoat(Number.parseInt(boatId, 10), boat?.label ?? '');
        eventAccumulator.push(...events);
      }
      const fileAccumulator: ResourceOption[] = [];
      for (const event of eventAccumulator) {
        const files = await fetchFilesForEvent(event.id, event.label);
        fileAccumulator.push(...files);
      }
      setOwnedEvents(eventAccumulator);
      setSelectedEventIds((previous) => {
        const next = new Set(
          [...previous].filter((id) => eventAccumulator.some((event) => String(event.id) === id)),
        );
        return setsAreEqual(previous, next) ? previous : next;
      });
      setOwnedFiles(fileAccumulator);
      setSelectedFileIds((previous) => {
        const next = new Set(
          [...previous].filter((id) => fileAccumulator.some((file) => String(file.id) === id)),
        );
        return setsAreEqual(previous, next) ? previous : next;
      });
    };
    void run();
  }, [fetchEventsForBoat, fetchFilesForEvent, ownedBoats, selectedBoatIds]);

  // When event selection changes (without boat change), fetch files for new events only
  useEffect(() => {
    const run = async () => {
      const fileAccumulator: ResourceOption[] = [];
      for (const eventId of selectedEventIds) {
        const event = ownedEvents.find((e) => String(e.id) === eventId);
        const files = await fetchFilesForEvent(Number.parseInt(eventId, 10), event?.label ?? '');
        fileAccumulator.push(...files);
      }
      setOwnedFiles(fileAccumulator);
      setSelectedFileIds((previous) => {
        const next = new Set(
          [...previous].filter((id) => fileAccumulator.some((file) => String(file.id) === id)),
        );
        return setsAreEqual(previous, next) ? previous : next;
      });
    };
    void run();
  }, [fetchFilesForEvent, ownedEvents, selectedEventIds]);

  const handleShareSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!apiBase) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_API));
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_TOKEN));
      return;
    }
    if (!form.email) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_SHARE_LABEL));
      return;
    }
    // Determine target based on depth selection (only share the deepest selected resources)
    const targets: Array<{ resourceType: 'boat' | 'event' | 'file'; resourceId: number }> = [];
    if (selectedFileIds.size > 0) {
      for (const id of selectedFileIds) {
        const value = Number.parseInt(id, 10);
        if (Number.isFinite(value)) {
          targets.push({ resourceType: 'file', resourceId: value });
        }
      }
    } else if (selectedEventIds.size > 0) {
      for (const id of selectedEventIds) {
        const value = Number.parseInt(id, 10);
        if (Number.isFinite(value)) {
          targets.push({ resourceType: 'event', resourceId: value });
        }
      }
    } else {
      for (const id of selectedBoatIds) {
        const value = Number.parseInt(id, 10);
        if (Number.isFinite(value)) {
          targets.push({ resourceType: 'boat', resourceId: value });
        }
      }
    }

    if (targets.length === 0) {
      showMessage('error', t(K_NOTIF_ERROR), t(SELECT_RESOURCE_MESSAGE, 'Selecciona un recurso'));
      return;
    }
    setIsLoading(true);
    try {
      let allSuccess = true;
      for (const target of targets) {
        const response = await fetch(`${apiBase}/api/sharing/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: form.email.trim(),
            resourceType: target.resourceType,
            resourceId: target.resourceId,
            canViewComments: form.canViewComments,
          }),
        });
        const json: unknown = await response.json();
        const data = json as { success?: boolean; message?: string };
        if (!data?.success) {
          allSuccess = false;
          showMessage('error', t(K_NOTIF_ERROR), data.message ?? t(K_ERR_UNEXPECTED));
        }
      }
      if (allSuccess) {
        showMessage('success', t(K_NOTIF_SUCCESS), t('settings.privacy.settingsUpdated'));
        setForm((previous) => ({ ...previous, email: '' }));
        await fetchSharingOverview();
      }
    } catch (error) {
      console.error('Error creating share:', error);
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_UNEXPECTED));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (item: SharingListItem, email: string) => {
    if (!apiBase) {
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${apiBase}/api/sharing/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          resourceType: item.resourceType,
          resourceId: item.resourceId,
          email,
        }),
      });
      const json: unknown = await response.json();
      const data = json as { success?: boolean; message?: string };
      if (data?.success) {
        await fetchSharingOverview();
      } else {
        showMessage('error', t(K_NOTIF_ERROR), data.message ?? t(K_ERR_UNEXPECTED));
      }
    } catch (error) {
      console.error('Error revoking share:', error);
    }
  };

  const handleCommentPermissionChange = async (
    item: SharingListItem,
    user: SharedUser,
    canViewComments: boolean,
  ) => {
    if (!apiBase) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const updateKey = `${item.resourceType}:${item.resourceId}:${user.id}`;
    setUpdatingCommentPermission(updateKey);
    try {
      const response = await fetch(`${apiBase}/api/sharing/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          resourceType: item.resourceType,
          resourceId: item.resourceId,
          email: user.email,
          canViewComments,
        }),
      });
      const json: unknown = await response.json();
      const data = json as { success?: boolean; message?: string };
      if (!data?.success) {
        showMessage('error', t(K_NOTIF_ERROR), data.message ?? t(K_ERR_UNEXPECTED));
        return;
      }
      setSharedByMe((previous) =>
        previous.map((entry) =>
          entry.resourceType === item.resourceType && entry.resourceId === item.resourceId
            ? {
                ...entry,
                sharing: entry.sharing
                  ? {
                      ...entry.sharing,
                      sharedWith: entry.sharing.sharedWith?.map((recipient) =>
                        recipient.id === user.id ? { ...recipient, canViewComments } : recipient,
                      ),
                    }
                  : entry.sharing,
              }
            : entry,
        ),
      );
      // Open map panels in this tab revalidate immediately. Other tabs do the
      // same when they regain focus, so a revoked permission is not kept in a
      // long-lived in-memory comments list.
      window.dispatchEvent(new Event('sailtrack:comment-permission-changed'));
    } catch (error) {
      console.error('Error updating comment permission:', error);
      showMessage('error', t(K_NOTIF_ERROR), t(K_ERR_UNEXPECTED));
    } finally {
      setUpdatingCommentPermission(null);
    }
  };

  const renderSharedItem = (item: SharingListItem, canManage: boolean) => {
    const label = buildSharingLabel(item.sharing, translateSharing);
    const resourceLabelKey = RESOURCE_TYPE_LABEL_KEYS[item.resourceType];
    const resourceLabel = t(resourceLabelKey, RESOURCE_TYPE_LABEL_FALLBACKS[item.resourceType]);
    const ownerName = getSharedUserDisplayName(item.sharing?.owner);
    const ownerLabel =
      !canManage && ownerName
        ? `${t(K_SHARED_BY_LABEL, 'Compartido por')}: ${ownerName}`
        : null;
    return (
      <div key={`${item.resourceType}-${item.resourceId}`} className="share-card">
        <div className="share-card-header">
          <div className="share-type-pill">{resourceLabel}</div>
          {label && (
            <span className={`sharing-badge ${sharingBadgeClass(item.sharing)}`}>{label}</span>
          )}
        </div>
        <div className="share-card-title">{item.name}</div>
        {item.eventName && <div className="share-card-subtitle">{item.eventName}</div>}
        {item.boatName && <div className="share-card-subtitle">{item.boatName}</div>}
        {ownerLabel && <div className="share-card-owner">{ownerLabel}</div>}
        {canManage && item.sharing?.sharedWith && item.sharing.sharedWith.length > 0 && (
          <div className="share-recipients">
            {item.sharing.sharedWith.map((user) => {
              const updateKey = `${item.resourceType}:${item.resourceId}:${user.id}`;
              return (
              <span key={user.email} className="recipient-chip recipient-chip--comments">
                <span>{user.email}</span>
                <label className="recipient-comment-access">
                  <input
                    type="checkbox"
                    checked={user.canViewComments === true}
                    disabled={updatingCommentPermission === updateKey}
                    onChange={(event) =>
                      void handleCommentPermissionChange(item, user, event.target.checked)
                    }
                  />
                  <span>{t('sharing.commentsAccess', 'Comentarios')}</span>
                </label>
                <button type="button" onClick={() => handleRevoke(item, user.email)}>
                  {t('common.remove', 'Eliminar')}
                </button>
              </span>
              );
            })}
          </div>
        )}
        {!canManage && (
          <div className="share-recipients">
            <button
              type="button"
              className="recipient-chip recipient-chip-remove"
              onClick={() => handleRevoke(item, '')}
            >
              {t('sharing.removeAccess', 'Retirar acceso')}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-form-container-sharing">
      <div className="sharing-header">
        <h2 className="settings-form-title-sharing">{t(K_SHARED_DATA_TITLE, 'Datos compartidos')}</h2>
        <button
          className="refresh-button-sharing"
          onClick={() => void fetchSharingOverview()}
          disabled={isRefreshing}
        >
          {isRefreshing ? t('common.loading', 'Cargando...') : t('common.refresh', 'Actualizar')}
        </button>
      </div>

      <div className="sharing-form">
        <div className="form-row">
          <label htmlFor="share-email">{t(K_SHARE_LABEL, 'Compartir con')}</label>
          <input
            id="share-email"
            type="email"
            value={form.email}
            onChange={(event) => setForm((previous) => ({ ...previous, email: event.target.value }))}
            placeholder={t('sharing.emailPlaceholder', 'usuario@ejemplo.com')}
          />
        </div>
        <div className="form-row form-row-select">
          <label htmlFor="share-boat">{t(PLACEHOLDER_BOAT, 'Barco')}</label>
          <ConfigSelect
            id="share-boat"
            value=""
            options={boatSelectOptions}
            keepOpenOnSelect
            onChange={(value) => toggleSelection(value, setSelectedBoatIds)}
            placeholder={t(SELECT_RESOURCE_LABEL, 'Seleccionar')}
            selectedValues={selectedBoatIds}
            valueLabel={selectedCountLabel(selectedBoatIds.size)}
          />
        </div>
        <div className="form-row form-row-comment-permission">
          <label className="sharing-comment-permission" htmlFor="share-comments-access">
            <input
              id="share-comments-access"
              type="checkbox"
              checked={form.canViewComments}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, canViewComments: event.target.checked }))
              }
            />
            <span>{t('sharing.allowComments', 'Permitir comentarios')}</span>
          </label>
          <p>
            {t(
              'sharing.allowCommentsHint',
              'Puede ver, crear, editar y eliminar los comentarios e imágenes de este recurso.',
            )}
          </p>
        </div>
        <div className="form-row form-row-select">
          <label htmlFor="share-event">{t(PLACEHOLDER_EVENT, 'Evento')}</label>
          <ConfigSelect
            id="share-event"
            value=""
            options={eventSelectOptions}
            keepOpenOnSelect
            onChange={(value) => toggleSelection(value, setSelectedEventIds)}
            placeholder={t(SELECT_RESOURCE_LABEL, 'Seleccionar')}
            disabled={selectedBoatIds.size === 0 || eventSelectOptions.length === 0}
            selectedValues={selectedEventIds}
            valueLabel={selectedCountLabel(selectedEventIds.size)}
          />
        </div>
        <div className="form-row form-row-select">
          <label htmlFor="share-file">{t(PLACEHOLDER_FILE, 'Archivo')}</label>
          <ConfigSelect
            id="share-file"
            value=""
            options={fileSelectOptions}
            keepOpenOnSelect
            onChange={(value) => toggleSelection(value, setSelectedFileIds)}
            placeholder={t(SELECT_RESOURCE_LABEL, 'Seleccionar')}
            disabled={selectedEventIds.size === 0 || fileSelectOptions.length === 0}
            selectedValues={selectedFileIds}
            valueLabel={selectedCountLabel(selectedFileIds.size)}
          />
        </div>
        <button
          className="share-submit"
          onClick={handleShareSubmit}
          disabled={
            isLoading ||
            !form.email ||
            (selectedBoatIds.size === 0 && selectedEventIds.size === 0 && selectedFileIds.size === 0)
          }
        >
          {isLoading ? t('common.loading', 'Cargando...') : shareTargetLabel}
        </button>
      </div>

      <div className="sharing-columns">
        <div>
          <h3 className="sharing-column-title">{t(K_SHARED_DATA_TITLE, 'Datos compartidos')}</h3>
          <div className="sharing-grid">
            {sharedByMe.length === 0 && (
              <div className="empty-sharing-card">
                <p className="empty-sharing-title">{t('sharing.sharedByMe', 'Propio')}</p>
                <p className="empty-sharing-message">
                  {t('sharing.noSharedByMe', 'Aún no has compartido datos. Selecciona un recurso y añade un email.')}
                </p>
              </div>
            )}
            {sharedByMe.map((item) => renderSharedItem(item, true))}
          </div>
        </div>
        <div>
          <h3 className="sharing-column-title">{t('sharing.sharedWithMe', 'Compartido conmigo')}</h3>
          <div className="sharing-grid">
            {sharedWithMe.length === 0 && (
              <div className="empty-sharing-card">
                <p className="empty-sharing-title">{t('sharing.sharedWithMe', 'Compartido conmigo')}</p>
                <p className="empty-sharing-message">
                  {t('sharing.noSharedWithMe', 'Nadie ha compartido datos contigo todavía.')}
                </p>
              </div>
            )}
            {sharedWithMe.map((item) => renderSharedItem(item, false))}
          </div>
        </div>
      </div>
    </div>
  );
};
